export type ErrorCode =
  | 'VALIDATION_ERROR' | 'UNAUTHENTICATED' | 'FORBIDDEN' | 'NOT_FOUND' | 'CONFLICT' | 'GONE'
  | 'CONCURRENT_MODIFICATION' | 'IDEMPOTENCY_CONFLICT' | 'BUSINESS_RULE_VIOLATION'
  | 'DEPENDENCY_UNAVAILABLE' | 'INTERNAL_ERROR' | 'NETWORK_ERROR';

/** The backend answers every failure with `{ error: { code, message, details, requestId } }`. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly status: number,
    readonly details?: unknown,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors of a 400, as `{ field: message }` (class-validator messages start with the property name). */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    const d = this.details as { message?: unknown } | undefined;
    const list = Array.isArray(d?.message) ? (d.message as unknown[]) : [];
    for (const m of list) {
      if (typeof m !== 'string') continue;
      const field = m.split(' ')[0] ?? '';
      if (field && !out[field]) out[field] = m;
    }
    return out;
  }

  /** Preconditions that failed (e.g. invoice validation): `details.problems[]`. */
  get problems(): string[] {
    const p = (this.details as { problems?: unknown } | undefined)?.problems;
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === 'string') : [];
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isRetryable = (e: unknown) => !isApiError(e) || e.code === 'NETWORK_ERROR' || e.status === 503 || e.status === 429;
